/**
 * Property translator and enrichment functions for extended entity
 * types (Dimension, Multileader, Text, MText).
 *
 * Extracted from editorStore.ts — pure structural refactor.
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";
import type {
  DimensionStyle,
  MultileaderStyle,
} from "@dxf-ai-editor/drawing-model";
import {
  deserializeColor,
  THICKNESS_BEARING_ENTITY_TYPES,
} from "@dxf-ai-editor/drawing-model";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { canApplyProperty } from "@dxf-ai-editor/command-engine";
// Codex P2 (#417) — ONE TRUTH for the numeric handling of extended-entity
// property edits, shared with the SHELL validator (command-engine
// `extendedPropertyMutations.ts`): the label→field map, the schema-derived
// numeric-field classification, the per-field range bounds, and the
// `parseFiniteNumeric` grammar. Importing them here (instead of the former local
// copies + a narrower decimal regex) guarantees the document accepts/rejects the
// EXACT same numeric values the shell does for every mapped numeric field.
import {
  enumFieldsByType,
  getTypedExtendedGeometryOwnedLabels,
  getTypedExtendedGeometrySideEffectLabels,
  labelToFieldByType,
  numericFieldsByType,
  parseFiniteNumeric,
  satisfiesNumericConstraint,
  stringDomainFieldsByType,
  TYPED_COMMON_CUTOVER_LABELS,
} from "@dxf-ai-editor/command-engine";
import {
  resolveDimVarProperties,
  resolveMlVarProperties,
} from "./dxfGeometry";

/**
 * Enrich every Dimension shell entity in the given list with DIMVAR
 * property keys (DIMASZ, DIMTXT, DIMBLK1, DIMDEC, ...) resolved from the
 * referenced dimension style. The SVG renderer reads these keys via
 * `readDimStyle(entity.properties)` and falls back to defaults when they
 * are missing — so without this step changing a style has no visible
 * effect on already-rendered dimensions.
 *
 * Pure: returns a new array, mutates nothing.
 */
export function enrichDimensionEntities(
  entities: EntityPreview[],
  dimensionStyles: DimensionStyle[],
): EntityPreview[] {
  if (entities.length === 0) return entities;
  const stylesByName = new Map<string, DimensionStyle>();
  for (const s of dimensionStyles) stylesByName.set(s.name, s);
  let dirty = false;
  const next = entities.map((entity) => {
    if (entity.type !== "Dimension") return entity;
    const styleName = entity.properties["Dimension style"] ?? "Standard";
    const dimVars = resolveDimVarProperties(styleName, stylesByName);
    if (Object.keys(dimVars).length === 0) return entity;
    // Skip if every key already matches — this avoids unnecessary
    // re-renders when nothing has changed.
    let same = true;
    for (const [k, v] of Object.entries(dimVars)) {
      if (entity.properties[k] !== v) {
        same = false;
        break;
      }
    }
    if (same) return entity;
    dirty = true;
    return {
      ...entity,
      properties: { ...entity.properties, ...dimVars },
    };
  });
  return dirty ? next : entities;
}

/**
 * Enrich every Multileader shell entity in the given list with MLVAR
 * property keys (MLVAR_TEXT_HEIGHT, MLVAR_ARROWHEAD_BLOCK, MLVAR_LEADER_TYPE,
 * …) resolved from the referenced multileader style table. The SVG
 * renderer reads these keys from `entity.properties` and overlays them
 * with the entity's own override flags — without this enrichment a
 * style change would have no visible effect.
 *
 * Mirrors `enrichDimensionEntities`. Pure: returns a new array, mutates
 * nothing. Skips entities whose properties already match.
 */
export function enrichMultileaderEntities(
  entities: EntityPreview[],
  multileaderStyles: MultileaderStyle[],
): EntityPreview[] {
  if (entities.length === 0) return entities;
  const stylesByName = new Map<string, MultileaderStyle>();
  for (const s of multileaderStyles) stylesByName.set(s.name, s);
  let dirty = false;
  const next = entities.map((entity) => {
    if (entity.type !== "Multileader") return entity;
    const styleName = entity.properties["Multileader style"] ?? "Standard";
    const mlVars = resolveMlVarProperties(styleName, stylesByName);
    if (Object.keys(mlVars).length === 0) return entity;
    // Skip if every key already matches — this avoids unnecessary
    // re-renders when nothing has changed.
    let same = true;
    for (const [k, v] of Object.entries(mlVars)) {
      if (entity.properties[k] !== v) {
        same = false;
        break;
      }
    }
    if (same) return entity;
    dirty = true;
    return {
      ...entity,
      properties: { ...entity.properties, ...mlVars },
    };
  });
  return dirty ? next : entities;
}

/**
 * Translate UI property labels into geometry-document field patches for
 * extended entity types (Dimension, MText, Text, Multileader, etc.).
 *
 * `applySimpleGeometryPropertyChanges` only handles Line/Circle/Arc and
 * silently drops everything else. Without this translator, properties/apply
 * commands targeting "Dimension style", "Text height", and other extended
 * properties update only the shell preview and never reach the persisted
 * geometry document — so DXF export would still emit the old values.
 *
 * Returns null if the entity type isn't extended or no fields changed.
 */
// Common object-property labels share a single entity-field mapping
// because every geometry entity carries them via drawingEntityBaseSchema
// (layer/color/linetype/lineweight/transparency/linetypeScale). Of these
// only `color` (typed EntityColor union) and `linetypeScale` (DXF group 48,
// `z.number().optional()` on the base schema) need value coercion — the four
// remaining fields stay plain strings in the schema, so they bypass the
// numeric-coercion heuristic. `Thickness` is NOT here: it is a PER-TYPE field
// (not on the base schema), handled by `mergeCommonFieldsForType` below.
export const commonLabelToEntityField: Record<string, string> = {
  Layer: "layer",
  Color: "color",
  Linetype: "linetype",
  Lineweight: "lineweight",
  Transparency: "transparency",
  "Linetype Scale": "linetypeScale",
};

/**
 * Entity types whose typed schema carries a `thickness` field (DXF group 39).
 * Re-exported from the one-truth schema-owner set in `@dxf-ai-editor/
 * drawing-model` (`THICKNESS_BEARING_ENTITY_TYPES`) so this translator and the
 * undo-inverse reader (`geometryMutations.readCommonProps`) cannot drift apart.
 * This is the schema-carries-the-field gate ONLY — a thickness-bearing type
 * whose CHPROP capability still EXCLUDES Thickness (Dimension / Revision Cloud)
 * is filtered separately by `isCommonPropertyAllowedForType` below.
 */
const THICKNESS_BEARING_TYPES = THICKNESS_BEARING_ENTITY_TYPES;

/**
 * EPIC-COLOR-UNION S1 — produce the typed value to store on the entity field for
 * a common-property write-back. `color` is the typed `EntityColor` union, so a
 * property-grid string ("5" / "ByLayer" / "#rrggbb") must be coerced into it
 * BEFORE it lands on the entity — `assembleDrawingDocument` does NOT re-parse the
 * schema in PROD (it only validates invariants), so the schema's `z.preprocess`
 * coercion would not run on a raw-string patch. Every other common field
 * (layer / linetype / lineweight / transparency) stays a plain string.
 *
 * `deserializeColor` (a superset of `coerceColor`) restores BOTH a plain
 * property-grid string AND the lossless `!EC:` serialization that the undo
 * inverse for `properties/apply` carries for rich colours (`rgb` with a
 * coexisting aci/name, or `bookColor`) — see `geometryMutations.readCommonProps`.
 * A plain user string never starts with `!EC:`, so it falls straight through to
 * `coerceColor`; deserialization is idempotent, so an already-typed colour
 * passes through unchanged.
 */
export function commonFieldPatchValue(
  commonField: string,
  value: string,
): unknown {
  // `color` → typed EntityColor union (see doc comment above).
  if (commonField === "color") return deserializeColor(value);
  // `linetypeScale` (base, every type) and `thickness` (per-type) are NUMERIC
  // typed fields — coerce the property-grid string to a finite number. A
  // non-finite value (e.g. "abc") returns the NUMERIC_OMIT sentinel so the
  // caller drops the key (a no-op for that field), matching the per-type
  // finite-guard heuristic for entity-specific numerics below.
  if (commonField === "linetypeScale" || commonField === "thickness") {
    const n = Number(value);
    return Number.isFinite(n) ? n : NUMERIC_OMIT;
  }
  // The four remaining common fields (layer/linetype/lineweight/transparency)
  // stay plain strings — byte-identical to the prior pass-through.
  return value;
}

/**
 * Sentinel returned by `commonFieldPatchValue` for a non-finite numeric value
 * (`Number("abc")` → NaN). The merge helpers below OMIT the key when they see
 * it, so a malformed Thickness / Linetype Scale leaves the field untouched
 * while every other valid change in the same apply still lands.
 */
const NUMERIC_OMIT = Symbol("numeric-omit");

/**
 * Merge the COMMON object-property changes into a geometry patch. ONE TRUTH for
 * every common-field merge site (LWPOLYLINE/Spline/ArrayRect finalize, Block
 * `otherChanges`, the trailing generic loop) so the map-driven write-back is not
 * cloned three times.
 *
 *  (a) `commonLabelToEntityField` — Layer/Color/Linetype/Lineweight/Transparency/
 *      Linetype Scale ride drawingEntityBaseSchema (subject to the per-target
 *      gate in (c) — e.g. Linetype / Linetype Scale on MText).
 *  (b) `Thickness → thickness` ONLY for the thickness-bearing type set (per-type
 *      schema field, DXF group 39) — never written onto a type whose schema
 *      lacks it (and further gated by (c) — Dimension / RevCloud).
 *  (c) Codex P2-1/P2-2 (PR #403) — EVERY common field is additionally gated by
 *      the SHELL's FULL per-target predicate `canApplyProperty` (reused VERBATIM
 *      from command-engine `propertyMutations.ts` — ONE TRUTH). That gate is
 *      capability AND exposure:
 *        (i)  the CHPROP capability list includes the label (per-type override
 *             table — excludes e.g. Linetype Scale on MText/Hatch/Viewport,
 *             Thickness on Dimension/RevCloud), AND
 *        (ii) the property is EXPOSED on the TARGET preview
 *             (`editableProperties.includes(name) || name in properties`).
 *      (ii) is the P2-1 fix: an OPTIONAL common field absent on the target (e.g.
 *      `Linetype Scale` on a default entity whose `linetypeScale` is undefined →
 *      NOT in its preview `properties`, NOT in its editable list) is SKIPPED by
 *      the shell, so the document must skip it too. A capability-only gate wrote
 *      `linetypeScale` onto such a default entity → document/DXF vs shell+audit
 *      divergence. For an ALLOWED (target, field) the write is byte-identical to
 *      the prior pass-through.
 *
 * A non-finite numeric coercion (NUMERIC_OMIT) drops that one key; mutates
 * `patch` in place and returns the number of fields actually written (callers
 * that track an `any`/empty flag use the count, so a NUMERIC_OMIT no-op never
 * marks the patch dirty).
 *
 * DEFUSE S15 — the 6 NON-Color common labels (Layer/Linetype/Lineweight/
 * Transparency/Linetype Scale/Thickness) are CUT OVER to the typed writer
 * (`buildTypedCommonEdit`, command-engine): the `properties/apply` reducer strips
 * them (via `stripTypedCommonCutoverChanges` below) BEFORE calling this
 * translator, so this merge no longer double-writes them onto the typed document.
 * The `commonLabelToEntityField` map + this merge STAY INTACT for `Color` (still
 * on the legacy path until its own slice) and for any standalone / test caller
 * that has not stripped them.
 */
function mergeCommonFieldsForType(
  patch: Record<string, unknown>,
  changes: Array<{ name: string; value: string }>,
  previewEntity: EntityPreview,
): number {
  const thicknessBearing = THICKNESS_BEARING_TYPES.has(previewEntity.type);
  let written = 0;
  for (const change of changes) {
    let commonField = commonLabelToEntityField[change.name];
    // Thickness is per-type, so it is not in `commonLabelToEntityField`; route
    // it explicitly and only for the schema-bearing types.
    if (!commonField && change.name === "Thickness" && thicknessBearing) {
      commonField = "thickness";
    }
    if (!commonField) continue;
    // Codex P2-1/P2-2 (PR #403) — respect the SHELL's FULL per-target gate
    // (capability list AND exposure). Reuses the EXACT `canApplyProperty`
    // predicate the shell property path applies, so the document write matches
    // the shell skip for BOTH a capability-excluded field (Linetype Scale on
    // MText/Hatch/Viewport, Thickness on Dimension/RevCloud) AND an
    // optional-absent field the target does not expose (Linetype Scale on a
    // default entity whose linetypeScale is undefined → not in properties /
    // editable list).
    if (!canApplyProperty(previewEntity, change.name, "chprop")) continue;
    const coerced = commonFieldPatchValue(commonField, change.value);
    if (coerced === NUMERIC_OMIT) continue;
    patch[commonField] = coerced;
    written += 1;
  }
  return written;
}

/**
 * DEFUSE S15 common-props cutover — remove the 6 non-Color common labels the
 * typed writer (`buildTypedCommonEdit`) now OWNS from a `properties/apply` change
 * set, so `translateExtendedEntityPropertyChanges` → `mergeCommonFieldsForType`
 * no longer double-writes them to the typed document. Color + geometry-specific +
 * extended fields pass through unchanged (Color stays on the legacy path until its
 * own slice; the `commonLabelToEntityField` map is untouched). One-truth for
 * WHICH labels are cut over is `TYPED_COMMON_CUTOVER_LABELS` (command-engine,
 * shared with the writer) so the strip and the write can never drift apart.
 */
export function stripTypedCommonCutoverChanges<T extends { name: string }>(
  changes: ReadonlyArray<T>,
): T[] {
  return changes.filter((change) => !TYPED_COMMON_CUTOVER_LABELS.has(change.name));
}

/**
 * DEFUSE S15 Color cutover — remove the `Color` common label from a
 * `properties/apply` change set so `translateExtendedEntityPropertyChanges` →
 * `mergeCommonFieldsForType` no longer writes it to the typed document. Color now
 * lands via the dedicated typed writer (`buildTypedColorEdit`, command-engine) in
 * the reducer's Step 1c. Kept SEPARATE from `stripTypedCommonCutoverChanges`
 * (Color is deliberately NOT in `TYPED_COMMON_CUTOVER_LABELS` — that set is the
 * "6 non-Color" invariant the common writer + census guard rely on). The
 * `commonLabelToEntityField` map is UNTOUCHED (MATCHPROP persistability +
 * standalone/test callers still resolve Color → `color`); this strip only governs
 * the reducer's document write path. Applied on top of
 * `stripTypedCommonCutoverChanges` so all 7 common labels are removed before the
 * legacy translator runs (rendering `mergeCommonFieldsForType`'s common-label
 * branch inert until its deferred full deletion).
 */
export function stripTypedColorChange<T extends { name: string }>(
  changes: ReadonlyArray<T>,
): T[] {
  return changes.filter((change) => change.name !== "Color");
}

// DEFUSE S15 Slice A — `parseCoordinatePairList` DELETED. It existed only for
// the LWPOLYLINE `Vertices` branch (deleted with the cutover); the strict
// all-or-nothing / exact-arity / count-locked parse now lives in the typed
// descriptor's `toRaw` (`lwpolylineDescriptors` → `pointArray` with
// `allowZ:false`, command-engine `descriptors/curves.ts`).
//
// DEFUSE S15 Slice D — `parseSplinePointList` + `parseNumberList` DELETED. They
// existed only for the SPLINE branch (deleted with the cutover); the 2-D/3-D
// point parse (count-UNLOCKED) + the all-or-nothing numeric-list parse now live
// in the typed descriptors' `toRaw` (`splineDescriptors` → count-unlocked
// `pointArray` for Control/Fit points + `numberArray` for Knots/Weights,
// command-engine `descriptors/curves.ts`); the fit→control recompute moved to
// `splineFitRecomputeMutator` (command-engine `sideEffectMutators.ts`).

// DEFUSE S15 Slice A — `parseBulgeListStrict` DELETED. It existed only for the
// LWPOLYLINE `Bulges` branch (deleted with the cutover); the strict
// all-or-nothing count-locked parse (`/[;,]/` split, empty/non-finite token
// reject, count == live vertex count) now lives verbatim in the typed `Bulges`
// descriptor's `toRaw` (`lwpolylineDescriptors`, command-engine
// `descriptors/curves.ts` — the `vertices[].bulge` array-map write).

/**
 * Merge the COMMON object-property changes (Layer/Color/Linetype/Linetype
 * Scale/Thickness/…) into a geometry patch and finalize it (return the patch,
 * or null if it ended up empty). One-truth for the SPLINE + HATCH
 * geometry-persist branches below (the LWPOLYLINE + ARRAYRECT branches were
 * cut over to the typed writer in S15 Slice A) so their tail (common-field
 * merge + empty→null) is not cloned. Delegates the common-field write-back to
 * `mergeCommonFieldsForType` (the shared map-driven merge, per-type Thickness
 * aware).
 */
function finalizeGeometryPatch(
  patch: Record<string, unknown>,
  changes: Array<{ name: string; value: string }>,
  previewEntity: EntityPreview,
): Partial<DrawingEntity> | null {
  mergeCommonFieldsForType(patch, changes, previewEntity);
  return Object.keys(patch).length > 0
    ? (patch as Partial<DrawingEntity>)
    : null;
}

/**
 * Mirror of command-engine `formatNumber` (geometryEntityTypes.ts:
 * `String(Number(value.toFixed(3)))`) — the SAME 3-decimal rounding the
 * hatch preview builder applies to Scale / Angle (`deriveHatchPreviewProperties`
 * → `formatNumber(entity.patternScale)`). Used by the HATCH branch's P2-e
 * raw-precision reconcile: when the palette re-submits the UNCHANGED rounded
 * display of a doc value, the translator must recognise it as "no real change"
 * and keep the raw doc value. `formatNumber` is NOT re-exported from the
 * command-engine public barrel, so it is replicated here byte-for-byte (one
 * truth = the rounding rule, not the function instance — kept in lock-step with
 * the previewBuilders consumer). A drift would only re-introduce the rounding
 * loss this guards against, which the P2-e test catches.
 */
function formatNumberLike(value: number): string {
  return String(Number(value.toFixed(3)));
}

// Codex P2 (#417) — `numericFieldConstraints` + `satisfiesNumericConstraint`
// (per-field range bounds) now live in the SHARED command-engine module
// `numericFieldClassification.ts` (imported above) so the SHELL validator and
// this DOCUMENT translator enforce the SAME bound for the SAME field. Removed the
// former local copies to keep ONE truth.

/**
 * Coerce a property-grid string value into the primitive type expected by
 * the customProperties record on BlockInsertEntity (string | number |
 * boolean). Numeric-looking values become numbers; "Yes"/"No"/"true"/
 * "false" become booleans; everything else stays string.
 */
function coerceParamValue(value: string): string | number | boolean {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  if (lower === "yes" || lower === "true") return true;
  if (lower === "no" || lower === "false") return false;
  if (trimmed === "") return "";
  const n = Number(trimmed);
  if (Number.isFinite(n) && /^-?\d+(?:\.\d+)?$/.test(trimmed)) return n;
  return value;
}

// Codex P2 (#417) — `labelToFieldByType` (UI label → typed schema field) and
// `numericFieldsByType` (schema-derived numeric classification) now live in the
// SHARED command-engine module `numericFieldClassification.ts` (imported above)
// so the SHELL validator and this DOCUMENT translator classify the SAME fields
// identically. Re-exported here so existing consumers (the `driftGuard` /
// `propertyTranslator` tests, `editorStore`, `geometryReducer`,
// `gripDragTranslate`) keep their `./propertyTranslator` import path unchanged —
// single source of truth, no hand-copy.
export { enumFieldsByType, labelToFieldByType, numericFieldsByType };

/**
 * S16-ARRAYRECT-READONLY-ALIGN (W2.1) defense-in-depth predicate — true when
 * `name` is the Rectangular Associative Array `Associative` label, which this
 * translator must NEVER write: AutoCAD does not let Properties flip an
 * existing array's associativity (only EXPLODE does), mirroring the
 * descriptor's static `readOnly: true` (command-engine
 * `descriptors/containers.ts`). Rows/Columns/Row spacing/Column spacing are
 * NOT graph-owned here — AW-1 (#535, `dxfBridge.arrayAssocExport.ts`
 * `applyAssocArrayWriteBack`) already surgically write-backs any dirty field
 * of that 8-field matrix on export, associative or not.
 */
function isArrayRectGraphOwnedLabel(
  name: string,
  previewEntity: EntityPreview,
): boolean {
  return (
    previewEntity.type === "Rectangular Associative Array" &&
    name === "Associative"
  );
}

/**
 * Labels the dedicated per-type branches of
 * `translateExtendedEntityPropertyChanges` (below) actually write to the
 * geometry document. Mirrors those branch `case`/`if` labels — kept adjacent so
 * a new branch label is registered here too. Only entity types with a non-empty
 * MATCHPROP copyable channel strictly need coverage (the resolution filter only
 * probes matchprop-copyable labels), but the geometry-only grip labels
 * (Vertices, Control points, …) are included for completeness so the predicate
 * never under-reports a persistable field.
 *
 * DEFUSE S15 Slice A — the LWPOLYLINE + Rectangular Associative Array entries
 * are NOT hand-mirrored branch sets any more (those branches are DELETED):
 * they derive from the ONE-TRUTH typed descriptor registry via
 * `getTypedExtendedGeometryOwnedLabels` (command-engine) — exactly the labels
 * the live write path (geometryReducer Step 1d → `buildTypedExtendedGeometry-
 * Edit`) actually persists. Of the three newly-persisting LWPOLYLINE labels
 * only `Constant width` and `Linetype generation` can actually FLOW through
 * MATCHPROP — they are in `copyableInMatchpropSpecial` (AutoCAD's polyline-
 * special channel copies width + plinegen); `Elevation` persists via
 * Properties/apply but is NOT matchprop-copyable (AutoCAD does not copy
 * polyline elevation through MATCHPROP). The channel/bag/descriptor triangle
 * is pinned by the census in propertyCapabilities.test.ts.
 */
const specialBranchDocumentLabels: Record<string, ReadonlySet<string>> = {
  // S15 Slice E — the registry-derived owned labels (plain: Scale / Angle /
  // Associative / Elevation; side-effect: Pattern / Solid) the typed writer now
  // persists, PLUS the still-legacy enum leaves (Hatch style / Pattern type) the
  // retained HATCH branch persists. `Gradient` stays NON-persistable (deferred
  // structural field — see isLabelDocumentPersistable). `Solid` / `Elevation`
  // persist via Properties/apply but are not MATCHPROP-copyable (not in the
  // hatch-special channel), exactly like LWPOLYLINE `Elevation`.
  Hatch: new Set([
    ...getTypedExtendedGeometryOwnedLabels("Hatch"),
    ...getTypedExtendedGeometrySideEffectLabels("Hatch"),
    "Hatch style",
    "Pattern type",
  ]),
  LWPOLYLINE: getTypedExtendedGeometryOwnedLabels("LWPOLYLINE"),
  // S15 Slice D — registry-derived like LWPOLYLINE/Hatch (the DELETED Spline
  // branch's hand-mirrored set is gone). PLAIN owned = Rational / Control points /
  // Knots / Weights; SIDE-EFFECT owned = Fit points — exactly the labels
  // geometryReducer Step 1d / 1d-se persist. Degree / Closed are READ-ONLY-calc
  // (the NURBS well-formedness invariant couples degree to the knot vector), so
  // they are NOT persistable — matching the legacy set (which never wrote them).
  // Spline has no matchprop-special channel, so this feeds only the persistability
  // predicate's completeness (never under-reports a persistable field).
  Spline: new Set([
    ...getTypedExtendedGeometryOwnedLabels("Spline"),
    ...getTypedExtendedGeometrySideEffectLabels("Spline"),
  ]),
  // Codex P2 round 5 (array-spec-render-arm) — "Associative" moved from the
  // PLAIN owned set to SIDE-EFFECT owned (arrayAssociativeMutator: converts
  // the coupled axisAngle unit on toggle). Mirrors the Hatch/Spline pattern
  // above — without the union, "Associative" would silently drop OUT of
  // `isLabelDocumentPersistable`, under-reporting a label the typed writer
  // (geometryReducer Step 1d-se) still genuinely persists.
  "Rectangular Associative Array": new Set([
    ...getTypedExtendedGeometryOwnedLabels("Rectangular Associative Array"),
    ...getTypedExtendedGeometrySideEffectLabels("Rectangular Associative Array"),
  ]),
  // S15 Slice B — Point + Ellipse first-time writability: registry-derived like
  // LWPOLYLINE/ArrayRect above (exactly the labels geometryReducer Step 1d
  // really persists — Position X/Y/Z + Rotation; Center X/Y, Major axis X/Y,
  // Ratio, Start/End parameter). Neither kind has a matchprop-special channel,
  // so these entries matter for the persistability predicate's completeness
  // ("never under-reports a persistable field"), not for MATCHPROP resolution.
  Point: getTypedExtendedGeometryOwnedLabels("Point"),
  Ellipse: getTypedExtendedGeometryOwnedLabels("Ellipse"),
  // S15 Slice F — VIEWPORT is DELIBERATELY NOT added here. The viewport typed
  // writer (geometryReducer Step 1d / 1d-se) DOES persist the owned labels
  // (Center X/Y, Width, Height, View twist + the side-effect Scale / Display lock /
  // View height / View center X/Y) through a DIRECT Properties/apply. But this
  // predicate is consumed by MATCHPROP resolution (matchpropResolution.ts), and the
  // viewport-special labels Scale / Display lock / Snap / Grid / UCS icon ARE in
  // `copyableInMatchpropSpecial` (propertyCapabilities.ts) — so advertising them
  // here would let MATCHPROP COPY them, which AutoCAD does NOT (viewport-special
  // MATCHPROP copy is a DEFERRED family-wide subsystem — "none of the six persist
  // via copy yet"). Keeping Viewport OUT of this map leaves MATCHPROP dropping the
  // viewport-special labels (unchanged) while a direct Properties/apply still
  // persists them via Step 1d-se, which never consults this predicate.
  // S15 Slice C — Text / MText / Dimension scalar-style tail: registry-derived
  // like the Slice A/B kinds (exactly the labels geometryReducer Step 1d really
  // persists). Covers the ADDITIVE leaves (Text Width factor / Insertion+
  // Alignment coords, MText Background fill / Insertion, Dimension Extension
  // coords / Angle) which are NOT in `labelToFieldByType` — so the persistability
  // predicate never under-reports them. The parity leaves are ALSO in the
  // labelToFieldByType branch of `isLabelDocumentPersistable`; the registry set
  // is the one truth for the additive superset.
  //
  // GRIPS-DRAG-MORE-TYPES round 9 (local Codex review) — Text ALSO unions in
  // `getTypedExtendedGeometrySideEffectLabels`, mirroring the Hatch/Spline/
  // Rectangular-Associative-Array pattern above: "Insertion X"/"Insertion Y"
  // moved from PLAIN-owned to SIDE-EFFECT-owned (`textInsertionMoveMutator`,
  // sideEffectMutators.ts) so a justified Text's `alignmentPoint` shifts in
  // lockstep. Without this union they would silently drop OUT of
  // `isLabelDocumentPersistable`, under-reporting a label the typed writer
  // (geometryReducer Step 1d-se) still genuinely persists — the exact
  // "Associative" desync class the Rectangular Associative Array comment
  // above already documents for a different mutator.
  Text: new Set([
    ...getTypedExtendedGeometryOwnedLabels("Text"),
    ...getTypedExtendedGeometrySideEffectLabels("Text"),
  ]),
  MText: getTypedExtendedGeometryOwnedLabels("MText"),
  Dimension: getTypedExtendedGeometryOwnedLabels("Dimension"),
  // S15 Slice G — the 10 INSERT transform scalars are now registry-derived
  // (typed-writer-owned, geometryReducer Step 1d — Insertion X/Y, Scale X/Y/Z,
  // Rotation, Column/Row count, Column/Row spacing), no longer a hand-mirrored
  // branch label list, so the write path and the MATCHPROP advertisement cannot
  // drift apart (the same one-truth conversion Slice A/B/C/D/E did). "Visibility"
  // stays on the LEGACY translator special-branch (`visibilityState`), and the
  // dynamic Param:* keys ride the `customProperties` merge — both are NOT
  // typed-writer-owned this slice. "Name" (blockName) is DEFERRED and never written,
  // so it is correctly absent (not persistable); "Hyperlink" is not-yet-routed.
  Block: new Set([
    ...getTypedExtendedGeometryOwnedLabels("Block"),
    "Visibility",
  ]),
  // @codex F1 (P1) — legacy heavy POLYLINE / ACAD_TABLE / OLE2FRAME. Registry-
  // derived exactly like the Slice A/B/C/G kinds above: these are the labels the
  // typed writer (geometryReducer Step 1d) now really persists — Polyline
  // Closed / Linetype generation / Start+End segment width / Elevation, Table
  // Position X/Y/Z, OLE Frame Upper left + Lower right X/Y. For Polyline this
  // ALSO makes the polyline-special MATCHPROP channel real: its
  // `copyableInMatchpropSpecial` (width + linetype generation + closed, the same
  // bundle LWPOLYLINE declares) was resolved away by this predicate because no
  // document path carried the labels. Table / OLE Frame have EMPTY matchprop-
  // special channels, so their entries only keep the predicate from
  // under-reporting a persistable field (the Point / Ellipse rationale).
  Polyline: getTypedExtendedGeometryOwnedLabels("Polyline"),
  Table: getTypedExtendedGeometryOwnedLabels("Table"),
  "OLE Frame": getTypedExtendedGeometryOwnedLabels("OLE Frame"),
};

/**
 * @codex P2-A (#420) — does the geometry-document property translator carry a
 * REAL field mapping for `label` on an entity of `entityType`? The MATCHPROP →
 * `properties/apply` resolution (`matchpropResolution.ts`) filters its copied
 * labels through this predicate so it never emits a label the document silently
 * DROPS. The viewport-special channel (`Display lock`, `Scale`, `Snap`, `Grid`,
 * `UCS icon`), `Annotative`, `Plot Style`, `Material`, and Hatch `Gradient` are
 * copyable in the SHELL preview (transient AutoCAD MATCHPROP display) but their
 * MATCHPROP-COPY persistence is a DEFERRED subsystem — advertising them in the
 * resolved `properties/apply` would promise a MATCHPROP persistence that no-ops.
 * (S15 Slice F NOTE: the viewport `Scale` / `Display lock` / `Center X/Y` / `Width`
 * / `Height` / `View twist` / `View height` / `View center X/Y` DO persist via a
 * DIRECT Properties/apply through the typed writer — geometryReducer Step 1d/1d-se,
 * which never consults THIS predicate — but they are kept OUT of the persistable set
 * so MATCHPROP stays deferred, matching AutoCAD, which does not MATCHPROP-copy
 * viewport scale/lock. See the `Viewport` note in `specialBranchDocumentLabels`.)
 * This mirrors the persistence decision the live document write path actually makes:
 * the common object map, per-type Thickness, the per-type `labelToFieldByType` map,
 * the dedicated special-entity branches, and (S15 Slice A) the typed
 * extended-geometry writer's registry-derived label set — which now REALLY persists
 * LWPOLYLINE `Constant width` / `Elevation` / `Linetype generation`.
 * NB: persistable ≠ matchprop-copyable — of those three only `Constant width`
 * + `Linetype generation` are in the polyline-special MATCHPROP channel
 * (`copyableInMatchpropSpecial`); `Elevation` is persistable through a direct
 * Properties/apply only (AutoCAD MATCHPROP does not copy polyline elevation).
 *
 * NOTE: this is a schema-field-EXISTENCE gate only. The common-field exposure
 * gate (`canApplyProperty`) is applied PER-TARGET by the translator itself; a
 * common field (Color / Layer / …) maps for every type here, so treating it as
 * persistable never re-introduces a dangling label — the only labels excluded
 * are those with NO field on ANY relevant path.
 */
export function isLabelDocumentPersistable(
  entityType: string,
  label: string,
): boolean {
  if (commonLabelToEntityField[label] !== undefined) return true;
  if (
    label === "Thickness" &&
    THICKNESS_BEARING_TYPES.has(entityType as DrawingEntity["type"])
  ) {
    return true;
  }
  if (labelToFieldByType[entityType]?.[label] !== undefined) return true;
  return specialBranchDocumentLabels[entityType]?.has(label) ?? false;
}

export function translateExtendedEntityPropertyChanges(
  previewEntity: EntityPreview,
  changes: Array<{ name: string; value: string }>,
  // Codex P1-B — the LIVE geometry-document entity (authoritative source for
  // full-precision values the lossy preview drops). Optional so callers that
  // only edit common/extended scalar fields need not thread it; the SPLINE
  // point-persist + HATCH / generic doc-precision reconcile paths use it when
  // present (the LWPOLYLINE per-vertex consumer moved to the typed writer in
  // S15 Slice A).
  documentEntity?: DrawingEntity,
): Partial<DrawingEntity> | null {
  if (changes.length === 0) return null;

  // ── Dynamic block custom properties (DYN-1.10) ──
  // "Param: <name>" → customProperties[<name>] (merge, not overwrite).
  // "Visibility"    → visibilityState
  // Handled before the generic label→field map because the Param: keys
  // are not fixed strings, they depend on the authored parameter names.
  //
  // ── MINSERT grid & base-geometry fields (FIN.7) ──
  // "Column count", "Row count", "Column spacing", "Row spacing",
  // "Rotation", "Scale X", "Scale Y", "Scale Z", "Insertion X",
  // "Insertion Y" route directly into BlockInsertEntity top-level fields
  // so MINSERT grip drags (and Property Grid edits) survive a round-trip
  // through `patchEntityById` instead of only landing on the shell
  // preview.
  if (previewEntity.type === "Block") {
    // ── BLOCK INSERT transform scalars — DELETED (DEFUSE S15 Slice G) ──
    // The INSERT transform-scalar parsing (Insertion X/Y raw-preserve via
    // `readRawPoint`, Scale X/Y/Z, Rotation, MINSERT Column/Row count int/min:1,
    // Column/Row spacing) moved to the typed writer: geometryReducer
    // `properties/apply` Step 1d → `buildTypedExtendedGeometryEdit`
    // (command-engine), driven by `blockDescriptors`. The XY_PRESERVE semantics
    // (untouched-axis + group-30 Z kept at full document precision), the verbatim
    // finite-number Scale/Rotation/spacing sets, and the int/min:1 count validation
    // are now descriptor-encoded — an out-of-range count REJECTS per the writer's
    // uniform contract instead of the deleted branch's silent `Math.max(1, …)`
    // clamp (the SAME documented Slice-A ArrayRect divergence). All 10 owned labels
    // are STRIPPED from the change set before this translator
    // (`stripTypedExtendedGeometryChanges`), so this branch can never double-write
    // them (no `readRawPoint` / `blockFieldPatch` needed here anymore).
    // RETAINED here (NOT cut over): the dynamic-block channels — Param
    // (`customProperties`, merged with the existing map by the reducer) + Visibility
    // (`visibilityState`) — plus common object fields via `mergeCommonFieldsForType`.
    // "Name" (blockName) is DEFERRED (advertised-writable but the deleted branch
    // silently no-oped it — routing it needs target-block-def validation);
    // "Hyperlink" stays not-yet-routed (unregistered side-effect mutator). Parity
    // gate: `extGeomSliceG.parity.test.ts`.
    const customPropertyUpdates: Record<string, string | number | boolean> =
      {};
    let visibilityState: string | undefined;
    const otherChanges: Array<{ name: string; value: string }> = [];
    for (const change of changes) {
      if (change.name.startsWith("Param: ")) {
        const paramName = change.name.slice("Param: ".length);
        // Coerce numeric / boolean where obvious; otherwise string.
        customPropertyUpdates[paramName] = coerceParamValue(change.value);
        continue;
      }
      if (change.name === "Visibility") {
        visibilityState = change.value;
        continue;
      }
      otherChanges.push(change);
    }
    if (
      Object.keys(customPropertyUpdates).length > 0 ||
      visibilityState !== undefined
    ) {
      // Build a partial BlockInsertEntity patch. We must MERGE
      // customProperties with the existing map — `patchEntityById`
      // applies the patch as a shallow merge, which would replace
      // customProperties wholesale. The caller (geometryReducer's
      // properties/apply case) is responsible for reading the existing
      // entity and handing us the merged map. To keep the contract
      // simple, we signal the merge intention by returning a marker
      // object that geometryReducer recognises. For this we encode the
      // patch at the partial level: customProperties map here replaces
      // only the keys we touched — callers use a dedicated reducer
      // case (see properties/apply handling in geometryReducer) that
      // merges Parent.customProperties with the patch.
      const patch: Partial<DrawingEntity> = {} as Partial<DrawingEntity>;
      if (Object.keys(customPropertyUpdates).length > 0) {
        (patch as { customProperties?: typeof customPropertyUpdates })
          .customProperties = customPropertyUpdates;
      }
      if (visibilityState !== undefined) {
        (patch as { visibilityState?: string }).visibilityState =
          visibilityState;
      }
      // If there are other (common) changes, merge them through the SAME
      // one-truth common-field path (Block is NOT a thickness-bearing type, so
      // `mergeCommonFieldsForType` correctly skips Thickness for it).
      if (otherChanges.length > 0) {
        mergeCommonFieldsForType(
          patch as Record<string, unknown>,
          otherChanges,
          previewEntity,
        );
      }
      return patch;
    }
  }

  // ── LWPOLYLINE geometry — DELETED (DEFUSE S15 Slice A) ──
  // The dedicated LWPOLYLINE branch (Vertices zip-with-document / strict Bulges
  // / Closed) moved to the typed writer: geometryReducer `properties/apply`
  // Step 1d → `buildTypedExtendedGeometryEdit` (command-engine), driven by
  // `lwpolylineDescriptors` — which now ALSO persists Constant width /
  // Elevation / Linetype generation (this branch's early return silently
  // dropped them). Parity gates: `extGeomSliceA.parity.test.ts` (apps/web) +
  // `typedPropertyWriter.extendedGeometry.test.ts` (command-engine).

  // ── SPLINE geometry — DELETED (DEFUSE S15 Slice D) ──
  // The dedicated Spline branch (Control points / Fit points / Knots / Weights /
  // Rational — with the fit→control recompute gated on !cpChange && !knotsChange)
  // moved to the typed writer: geometryReducer `properties/apply` Step 1d (PLAIN
  // leaves Control points / Knots / Weights / Rational, via
  // `buildTypedExtendedGeometryEdit`) + Step 1d-se (`Fit points` →
  // `splineFitRecomputeMutator`, command-engine `sideEffectMutators.ts`, which
  // reproduces the fit→control recompute + weights=[]/rational=false reset).
  // Degree / Closed are READ-ONLY-calc (the NURBS well-formedness invariant
  // couples degree to the knot vector — a bare scalar write would vanish the curve
  // + export an ezdxf-rejected SPLINE), exactly the prior no-op (the legacy branch
  // never wrote them). The
  // Control points / Fit points descriptors are count-UNLOCKED (a fit-recompute /
  // undo changes the point count — the deleted `parseSplinePointList` had no count
  // lock). All cut-over labels are STRIPPED before this translator
  // (`stripTypedExtendedGeometryChanges`), so there is no double-write. Parity
  // gates: `extGeomSliceD.parity.test.ts` (apps/web) +
  // `sideEffectMutators.census.test.ts` (command-engine).

  // ── RECTANGULAR ASSOCIATIVE ARRAY grid — DELETED (DEFUSE S15 Slice A) ──
  // The dedicated ArrayRect branch (Rows/Columns int clamp, spacing/axis
  // numerics, Base X/Y raw-precision + Z preserve via `readRawPoint`,
  // Associative) moved to the typed writer: geometryReducer `properties/apply`
  // Step 1d → `buildTypedExtendedGeometryEdit` (command-engine), driven by
  // `arrayRectDescriptors` — the XY_PRESERVE / int / min:1 semantics are now
  // descriptor-encoded (an out-of-range count REJECTS per the writer contract
  // instead of the silent clamp-to-1). Parity gates: `extGeomSliceA.parity.
  // test.ts` (apps/web) + `typedPropertyWriter.extendedGeometry.test.ts`.

  // ── HATCH pattern fields (HATCHEDIT + Properties palette) ──────────
  // The `properties/apply` palette edit and the `geometry/hatchedit` CLI
  // command both feed this translator with the SAME preview-string labels
  // (`deriveHatchPreviewProperties` in command-engine: Pattern / Scale /
  // Angle / Associative / "Hatch style" / "Pattern type"). They map onto the
  // typed `hatchEntitySchema` (drawing-model) fields. The style/pattern-type
  // labels are HUMAN strings ("Normal"/"Outer"/"Ignore",
  // "User defined"/"Predefined"/"Custom") that must be converted BACK to the
  // DXF integer codes (groups 75 / 76) — the generic numeric heuristic below
  // cannot do that, so HATCH gets a dedicated branch (same shape as the
  // LWPOLYLINE / Spline / Array branches above). `Associative` is a Yes/No
  // boolean. Common object fields (Layer/Color/…) ride `finalizeGeometryPatch`.
  if (previewEntity.type === "Hatch") {
    // DEFUSE S15 Slice E — the HATCH branch's Pattern / Scale / Angle /
    // Associative cases were DELETED with the cutover. Pattern (+ the coupled
    // solidFill / gradient / customPatternLines derivation) + Solid now route
    // through the side-effect mutator (`hatchPatternMutator`, geometryReducer
    // Step 1d-se); Scale / Angle / Associative / Elevation through the plain
    // typed writer (Step 1d). All are STRIPPED from this change set before the
    // translator (`stripTypedExtendedGeometryChanges`), so this branch can never
    // double-write them. Only the label-decode ENUM leaves (Hatch style /
    // Pattern type, DXF 75 / 76) stay on the legacy path this slice — owning them
    // needs the enum-rule refinement + a `buildHatchPreview` import-factory
    // reconciliation (it emits neither key), a separable Cat-C follow-up
    // (Slice E2). They are HUMAN labels ("Normal"/"Outer"/"Ignore",
    // "User defined"/"Predefined"/"Custom") converted BACK to the DXF integer
    // codes — the reverse of `hatchStyleLabel` / `patternTypeLabel`
    // (command-engine previewBuilders.ts), ONE TRUTH for the label→code mapping;
    // a non-matching label falls back to a parsed integer, then the field default.
    const hatchStyleByLabel: Record<string, number> = {
      Normal: 0,
      Outer: 1,
      Ignore: 2,
    };
    const patternTypeByLabel: Record<string, number> = {
      "User defined": 0,
      Predefined: 1,
      Custom: 2,
    };
    const hatchPatch: Record<string, unknown> = {};
    for (const change of changes) {
      switch (change.name) {
        case "Hatch style": {
          // DXF group 75 — label or raw int.
          const code =
            hatchStyleByLabel[change.value.trim()] ??
            (Number.isInteger(Number(change.value))
              ? Number(change.value)
              : undefined);
          if (code !== undefined) hatchPatch.hatchStyle = code;
          break;
        }
        case "Pattern type": {
          // DXF group 76 — label or raw int.
          const code =
            patternTypeByLabel[change.value.trim()] ??
            (Number.isInteger(Number(change.value))
              ? Number(change.value)
              : undefined);
          if (code !== undefined) hatchPatch.patternType = code;
          break;
        }
        default:
          break;
      }
    }
    return finalizeGeometryPatch(hatchPatch, changes, previewEntity);
  }

  const map = labelToFieldByType[previewEntity.type] ?? {};
  const thicknessBearing = THICKNESS_BEARING_TYPES.has(previewEntity.type);
  // DEFUSE S15 Slice C — the typed extended-geometry writer (geometryReducer
  // Step 1d → `buildTypedExtendedGeometryEdit`) OWNS these labels for the cutover
  // kinds (Slice A LWPOLYLINE/ArrayRect; Slice B Point/Ellipse; Slice C Text/
  // MText/Dimension scalar-style tail). The generic `labelToFieldByType` WRITE
  // application is DELETED for them here — the MAP ITSELF stays (the shell numeric
  // validator's one truth via `classifyNumericLabel` / `numericFieldsByType`, so
  // the driftGuard test remains green). The reducer additionally STRIPS these
  // before the translator; this skip is the by-construction no-double-write
  // guarantee even for a DIRECT translator call (the parity test pins it: the
  // translator returns `null` for a pure cut-over change set). Derived path-""
  // coords (Dimension line/Third/Arc) are NOT owned, so they still flow to their
  // own no-op path; enum + naming-deferred leaves (MText Attachment/Direction/
  // Line spacing) are NOT owned either, so they keep their legacy write here.
  const ownedByTypedWriter = getTypedExtendedGeometryOwnedLabels(previewEntity.type);

  const patch: Record<string, unknown> = {};
  let any = false;
  for (const { name, value } of changes) {
    // S16-ARRAYRECT-READONLY-ALIGN (W2.1) DEFENSE-IN-DEPTH — the PRIMARY
    // reject is the `Associative` descriptor's static `readOnly: true`
    // (descriptors/containers.ts), enforced at the write boundary
    // (`applyTypedPropertyEditWithDescriptor`) that geometryReducer Step 1d
    // runs BEFORE this function is ever called — the side-effect-owned strip
    // already keeps this label from reaching the live `properties/apply`
    // path, so this branch is normally unreachable there. This is the
    // FAIL-CLOSED twin for any call site that reaches
    // `translateExtendedEntityPropertyChanges` directly (a test, or a future
    // caller bypassing Step 1d): console.warn mirrors the ARRAYRECT MAXARRAY
    // gate (`arrayReducers.ts`) so an edit AutoCAD's own Properties palette
    // never offers (associativity only changes via EXPLODE) is at least
    // OBSERVABLE, not silently swallowed.
    if (isArrayRectGraphOwnedLabel(name, previewEntity)) {
      console.warn(
        `PROPERTIES: "${name}" cannot be changed on a Rectangular Associative ` +
          `Array through Properties (AutoCAD parity — only EXPLODE changes ` +
          `associativity); edit skipped.`,
      );
      continue;
    }
    // Cut-over label — the typed writer owns the document write; never
    // double-write it through the generic map application below.
    if (ownedByTypedWriter.has(name)) continue;
    // Common object properties apply uniformly to every entity type. They
    // route through the SAME one-truth common-field merge as the geometry +
    // Block branches above (`mergeCommonFieldsForType`): Layer/Linetype/
    // Lineweight/Transparency stay plain strings; Color is the typed
    // `EntityColor` union; Linetype Scale + per-type Thickness are coerced to
    // a finite number (a non-finite value omits that one key). A single bad
    // Thickness/Linetype Scale therefore skips ONLY itself and the rest of the
    // apply still lands.
    if (
      commonLabelToEntityField[name] !== undefined ||
      (name === "Thickness" && thicknessBearing)
    ) {
      // `any` only flips when the merge actually wrote a key (NUMERIC_OMIT for
      // a malformed numeric writes nothing → no-op for that field).
      if (
        mergeCommonFieldsForType(patch, [{ name, value }], previewEntity) > 0
      ) {
        any = true;
      }
      continue;
    }

    // VIEWPORT "Shade plot" (DXF group 170) is DISPLAY-ONLY in the PROPERTIES
    // grid / MATCHPROP: the grid SHOWS the real mode but it is not copyable or
    // editable there, so there is intentionally NO label→field mapping for it.
    // The canonical persisting editor is the dedicated ViewportInspector dropdown
    // (command path: `applyViewportSetShadePlot` with full guards + faithful
    // undo). MATCHPROP/Properties-apply persistence for the six viewport-special
    // fields (Shade plot + Display lock/Scale/Snap/Grid/UCS icon) is a deferred
    // family-wide subsystem (multiple apply paths — toolbar propertyReducer,
    // shell, geometry translator — each with its own undo).

    const field = map[name];
    if (!field) continue;

    // @codex #487 P2 — a mapped ENUM field (Revision Cloud `creationMode`,
    // `z.enum(["Rectangular","Polygonal","Object"])`) must NEVER persist a value
    // outside its schema domain. The generic tail below writes a
    // non-numeric/non-boolean/non-string-exempt field VERBATIM, so a direct
    // `properties/apply` (agent/command API, CHPROP, or a scripted test) sending
    // `Creation mode: "Freehand"` (or a typo) would land an invalid enum on the
    // typed document — dev/test throws in `assembleDrawingDocumentInPlace`, and
    // PROD (which SKIPS that re-validation) persists an invalid
    // `RevisionCloudEntity` for export/render. OMIT the out-of-domain value (skip
    // this one key; the rest of the apply still lands; `any` NOT flipped) — the
    // SAME reject the shell makes via `classifyEnumLabel` (shell↔document parity).
    // The allowed domain is schema-derived (`enumFieldsByType`, one truth) so it
    // cannot drift from the zod enum. A value IN the domain falls through and is
    // written verbatim by the generic tail — unchanged behaviour for valid input.
    const enumAllowed = enumFieldsByType[previewEntity.type]?.[field];
    if (enumAllowed !== undefined && !enumAllowed.has(value)) continue;

    // @codex #487 P2 — a mapped string-DOMAIN field (Revision Cloud `Cloud style`
    // → `revisionCloudStyle`, `z.string().default("Normal")`) carries a FIXED
    // value set the schema stores as a plain `z.string()` — so it is NOT in
    // `numericFieldsByType` (not a ZodNumber) NOR in `enumFieldsByType` (not a
    // ZodEnum) and would otherwise fall through to the generic tail below, which
    // (a) COERCES a numeric-looking `"1"` to the NUMBER 1 (violating the
    // `z.string()` schema — dev/test throws, prod persists a non-string) and
    // (b) writes an out-of-domain `"Foo"` VERBATIM even though the DXF encoder
    // only supports Normal / Calligraphy and silently saves anything else as
    // Normal. Guard it here: KEEP the value string-typed AND OMIT an out-of-domain
    // value (skip this one key; the rest of the apply still lands; `any` NOT
    // flipped) — the SAME reject the shell makes via `classifyStringDomainLabel`
    // (shell↔document parity). The allowed domain is the ONE TRUTH
    // `stringDomainFieldsByType`; a value IN the domain is written as a string.
    const stringDomainAllowed = stringDomainFieldsByType[previewEntity.type]?.[field];
    if (stringDomainAllowed !== undefined) {
      if (!stringDomainAllowed.has(value)) continue;
      patch[field] = value;
      any = true;
      continue;
    }

    // Codex P2 (#417) — the typed schema field is NUMERIC (classified once from
    // the zod `.shape`, see `numericFieldsByType`). A known-numeric field is graded
    // by the SHARED `parseFiniteNumeric` grammar (see the dedicated branch after the
    // string-only / boolean short-circuits) so it accepts/omits the EXACT same set
    // as the SHELL validator — never the narrow legacy regex, which diverged.
    const isNumericField = numericFieldsByType[previewEntity.type]?.has(field) ?? false;

    // Boolean fields — "Yes"/"No"/"true"/"false"/"1"/"0" map to a
    // real boolean before the patch is handed to zod. Without this,
    // the heuristic below would coerce "1"/"0" to numbers, and the
    // multileaderEntitySchema would reject them.
    const booleanFields = new Set(["textFrameDisplay"]);
    if (booleanFields.has(field)) {
      const lower = value.trim().toLowerCase();
      const truthy =
        lower === "yes" || lower === "true" || lower === "on" || lower === "1";
      patch[field] = truthy;
      any = true;
      continue;
    }

    // String-only fields — these carry AutoCAD values like
    // "ByLayer", "ByBlock", "Default", block names etc. that look
    // neither numeric nor boolean. Force-keep as string so the zod
    // schema accepts them.
    //
    // `contents` (Text / MText, model.ts `z.string()`) and `textContents`
    // (Multileader, model.ts `z.string().default("")`) are FREE TEXT that may
    // LOOK numeric ("123", "3.14", "007"). Without this exemption the generic
    // numeric-coercion heuristic below would emit `{ contents: 123 }` (a NUMBER)
    // for a digit-only label, which the `z.string()` schema rejects (dev/test
    // throws; prod carries a non-string the render/export code reads as text) and
    // which silently drops a meaningful leading zero. They MUST always stay
    // strings — the prior `Contents → text` mapping was only implicitly safe
    // because `text` was a dead key that never reached coercion (PR #416 fixed it
    // to the real `contents`/`textContents` field, making this path reachable).
    const stringOnlyFields = new Set([
      "textColor",
      "leaderLineColor",
      "leaderLineWeight",
      "blockColor",
      "arrowheadBlock",
      "blockName",
      "contents",
      "textContents",
    ]);
    if (stringOnlyFields.has(field)) {
      patch[field] = value;
      any = true;
      continue;
    }

    // Codex P2 (#417) — KNOWN-NUMERIC schema field: grade with the SHARED
    // `parseFiniteNumeric` grammar (ONE TRUTH with the shell). It accepts `" 5 "`
    // / `".5"` / `"1e3"` (→ 5 / 0.5 / 1000) and rejects `""` / `"abc"` (→ null) —
    // the SAME accept/reject set the shell uses, replacing the former narrow
    // `/^-?\d+(?:\.\d+)?$/` regex that diverged (the shell accepted `" 5 "` while
    // the document OMITTED it). A non-finite value (null) is OMITTED so a STRING
    // never lands on a numeric field. Then the doc-precision reconcile + the
    // shared range-bound check run on the parsed number.
    if (isNumericField) {
      const asNumber = parseFiniteNumeric(value);
      // Non-finite ("" / "abc") → OMIT this one key (no-op; the rest of the apply
      // still lands; `any` NOT flipped). Matches the shell's reject so neither
      // side mutates while the other drops.
      if (asNumber === null) continue;

      // P2-2 — generic doc-precision reconcile (mirrors the HATCH P2-e Scale /
      // Angle branch). The Properties palette re-dispatches the FULL property
      // draft on every apply, with each numeric row `formatNumber`-rounded to 3
      // decimals. So an UNRELATED edit (Color/Contents) ALSO re-submits the
      // Height/Width rows; without this guard the generic coercion would rewrite
      // `nominalHeight`/`referenceWidth` from the rounded string, truncating an
      // imported MText's `nominalHeight: 2.123456` → 2.123. When the submitted
      // string is exactly the rounded display of the UNCHANGED doc value, KEEP
      // the raw full-precision doc value. Only an actual user change overwrites.
      // Covers EVERY mapped numeric field, not just MText (behaviour-safe: only
      // the unchanged-rounded-row case differs from before).
      const docValue = (documentEntity as Record<string, unknown> | undefined)?.[
        field
      ];
      if (
        documentEntity !== undefined &&
        typeof docValue === "number" &&
        Number.isFinite(docValue) &&
        value.trim() === formatNumberLike(docValue)
      ) {
        patch[field] = docValue;
        any = true;
        continue;
      }

      // P2-1 — validate the numeric value against the field's zod range bound
      // (the SHARED `numericFieldConstraints` / `satisfiesNumericConstraint`, the
      // SAME bound the shell enforces). `nominalHeight` is `z.number().positive()`
      // (rejects 0 / negatives), `referenceWidth` / Text `height` are
      // `z.number().nonnegative()` (reject negatives). A violating value is OMITTED
      // (skip this one key; the rest of the apply still lands; `any` is NOT flipped
      // for it) so dev/test does not throw in `assembleDrawingDocumentInPlace` and
      // prod never persists an invalid entity. Do NOT throw.
      if (!satisfiesNumericConstraint(field, asNumber)) {
        continue;
      }
      patch[field] = asNumber;
      any = true;
      continue;
    }

    // Non-numeric mapped field that is also not boolean / string-exempt. The
    // legacy heuristic keeps a numeric-LOOKING value as a number when it matches
    // the narrow decimal grammar (no such field is `z.ZodNumber` — those took the
    // `isNumericField` branch above — so this only affects e.g. a future
    // string-typed field a user typed digits into); otherwise keep the string.
    const asNumber = Number(value);
    const isNumeric =
      Number.isFinite(asNumber) && /^-?\d+(?:\.\d+)?$/.test(value);
    if (isNumeric) {
      patch[field] = asNumber;
      any = true;
      continue;
    }

    patch[field] = value;
    any = true;
  }
  return any ? (patch as Partial<DrawingEntity>) : null;
}
